import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export interface ReadingGoal {
  id: string;
  user_id: string;
  year: number;
  target_books: number;
  target_pages?: number;
  created_at: string;
  updated_at: string;
}

export function useReadingGoals() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);

  const getGoalForYear = async (year: number): Promise<ReadingGoal | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('reading_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', year)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching reading goal:', error);
      return null;
    }
  };

  const createOrUpdateGoal = async (year: number, target_books: number, target_pages?: number): Promise<ReadingGoal | null> => {
    if (!user) return null;

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('reading_goals')
        .upsert({
          user_id: user.id,
          year,
          target_books,
          target_pages
        }, { onConflict: 'user_id,year' })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Goal Updated",
        description: `Set goal of ${target_books} books for ${year}`,
      });

      return data;
    } catch (error) {
      console.error('Error updating reading goal:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update reading goal",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    getGoalForYear,
    createOrUpdateGoal
  };
}